import axiosInstance from '@/app/api/axiosInstance';
import { 
  Party, 
  PartyCreate, 
  PartyUpdate, 
  PartyGroup, 
  PartyGroupCreate,
  PartyGroupUpdate,
  PartyUser
} from '@/app/types/party';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import { sanitizePhone } from '@/app/utils/phone';

// Party endpoints
export const listParties = async (params: ListQueryParams, organizationId?: number): Promise<PaginatedResponse<Party>> => {
  try {
    // Prepare request parameters
    const requestParams = { ...params };
    
    // Handle filters - merge with organization_id filter
    if (params.filters && typeof params.filters === 'string') {
      try {
        const existingFilters = JSON.parse(params.filters);
        // Add organization_id filter if not already present
        if (organizationId && !existingFilters.some((f: any) => f.field === 'organization_id')) {
          existingFilters.push({
            field: 'organization_id',
            op: 'eq',
            value: organizationId
          });
        }
        requestParams.filters = JSON.stringify(existingFilters);
      } catch (e) {
        // If parsing fails, just use the original filters
        requestParams.filters = params.filters;
      }
    } else if (organizationId) {
      // No filters provided, create with just organization_id
      requestParams.filters = JSON.stringify([{
        field: 'organization_id',
        op: 'eq',
        value: organizationId
      }]);
    }
    
    const response = await axiosInstance.get<PaginatedResponse<Party>>('/parties/', { 
      params: requestParams 
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to list parties:', error);
    throw error;
  }
};

/**
 * Canonical React Query key for a single party. Use this everywhere a party is
 * fetched by id so the various callers on one screen share a cache entry
 * instead of each firing its own identical GET.
 */
export const partyQueryKey = (partyId: number | null | undefined) => ['party', partyId] as const;

export const getParty = async (partyId: number): Promise<Party> => {
  try {
    const response = await axiosInstance.get<Party>(`/parties/${partyId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get party with ID ${partyId}:`, error);
    throw error;
  }
};

export const createParty = async (data: PartyCreate, organizationId?: number): Promise<Party> => {
  try {
    // Ensure organization_id is set. Phone is stripped of whitespace here too so
    // every caller is covered, not just the party form — the client login id is
    // derived from it and logins reject spaces.
    const partyData = {
      ...data,
      ...(data.phone !== undefined ? { phone: sanitizePhone(data.phone) } : {}),
      organization_id: organizationId
    };
    
    const response = await axiosInstance.post<Party>('/parties/', partyData);
    return response.data;
  } catch (error) {
    console.error('Failed to create party:', error);
    throw error;
  }
};

export const updateParty = async (partyId: number, data: PartyUpdate): Promise<Party> => {
  try {
    const payload = data.phone !== undefined ? { ...data, phone: sanitizePhone(data.phone) } : data;
    const response = await axiosInstance.patch<Party>(`/parties/${partyId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Failed to update party with ID ${partyId}:`, error);
    throw error;
  }
};

export const deleteParty = async (partyId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/parties/${partyId}`);
  } catch (error) {
    console.error(`Failed to delete party with ID ${partyId}:`, error);
    throw error;
  }
};

// Party Group endpoints
export const listPartyGroups = async (params: ListQueryParams, organizationId?: number): Promise<PaginatedResponse<PartyGroup>> => {
  try {
    // Prepare request parameters
    const requestParams = { ...params };
    
    // Handle filters - merge with organization_id filter
    if (params.filters && typeof params.filters === 'string') {
      try {
        const existingFilters = JSON.parse(params.filters);
        // Add organization_id filter if not already present
        if (organizationId && !existingFilters.some((f: any) => f.field === 'organization_id')) {
          existingFilters.push({
            field: 'organization_id',
            op: 'eq',
            value: organizationId
          });
        }
        requestParams.filters = JSON.stringify(existingFilters);
      } catch (e) {
        // If parsing fails, just use the original filters
        requestParams.filters = params.filters;
      }
    } else if (organizationId) {
      // No filters provided, create with just organization_id
      requestParams.filters = JSON.stringify([{
        field: 'organization_id',
        op: 'eq',
        value: organizationId
      }]);
    }
    
    const response = await axiosInstance.get<PaginatedResponse<PartyGroup>>('/parties/groups/', { 
      params: requestParams 
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to list party groups:', error);
    throw error;
  }
};

export const getPartyGroup = async (groupId: number): Promise<PartyGroup> => {
  try {
    const response = await axiosInstance.get<PartyGroup>(`/parties/groups/${groupId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get party group with ID ${groupId}:`, error);
    throw error;
  }
};

export const createPartyGroup = async (data: PartyGroupCreate, organizationId?: number): Promise<PartyGroup> => {
  try {
    // Ensure organization_id is set if not provided in data
    const groupData = {
      ...data,
      organization_id: organizationId
    };
    
    const response = await axiosInstance.post<PartyGroup>('/parties/groups/', groupData);
    return response.data;
  } catch (error) {
    console.error('Failed to create party group:', error);
    throw error;
  }
};

export const updatePartyGroup = async (groupId: number, data: PartyGroupUpdate): Promise<PartyGroup> => {
  try {
    const response = await axiosInstance.patch<PartyGroup>(`/parties/groups/${groupId}`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update party group with ID ${groupId}:`, error);
    throw error;
  }
};

export const deletePartyGroup = async (groupId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/parties/groups/${groupId}`);
  } catch (error) {
    console.error(`Failed to delete party group with ID ${groupId}:`, error);
    throw error;
  }
};

// Client User endpoints
export interface ClientUserResponse {
  user_id: number;
  username: string;
  pin: string;
  party_id: number;
}

export const createClientUser = async (partyId: number): Promise<ClientUserResponse> => {
  try {
    const response = await axiosInstance.post<ClientUserResponse>(`/parties/${partyId}/create-client-user`);
    return response.data;
  } catch (error) {
    console.error(`Failed to create client user for party ${partyId}:`, error);
    throw error;
  }
};

// PIN resets go through resetUserPin() in userManagementService, which targets an
// explicit user. The party-level POST /parties/{id}/reset-client-pin resets
// whichever linked user happens to come first, and parties can have several.

// Multi-Party User Management endpoints
export interface AssignUserRequest {
  user_id: number;
}

export interface AssignUserResponse {
  message: string;
}

export const assignUserToParty = async (partyId: number, userId: number): Promise<AssignUserResponse> => {
  try {
    const response = await axiosInstance.post<AssignUserResponse>(
      `/parties/${partyId}/assign-user`,
      { user_id: userId }
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to assign user ${userId} to party ${partyId}:`, error);
    throw error;
  }
};

export const unassignUserFromParty = async (partyId: number, userId: number): Promise<AssignUserResponse> => {
  try {
    const response = await axiosInstance.delete<AssignUserResponse>(
      `/parties/${partyId}/unassign-user`,
      { data: { user_id: userId } }
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to unassign user ${userId} from party ${partyId}:`, error);
    throw error;
  }
};

// Get users assigned to a party (lazy-loaded)
export const getPartyUsers = async (partyId: number): Promise<PartyUser[]> => {
  try {
    const response = await axiosInstance.get<PartyUser[]>(`/parties/${partyId}/users`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get users for party ${partyId}:`, error);
    throw error;
  }
};